import { Button } from '@/components/ui/Button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/Collapsible';
import Loader from '@/components/ui/Loader';
import withLocation from '@/components/withLocation';
import { getUserProfileDisplayName } from '@/lib/helpers/userProfile';
import { WindowLocation } from '@reach/router';
import { Link } from 'gatsby';
import isEmpty from 'lodash/isEmpty';
import { Search, SquareArrowOutUpRight, User, X } from 'lucide-react';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import getUserAction from '../../app/redux/actions/getUserAction';
import signOutAction from '../../app/redux/actions/signOutAction';
import updateLastAction from '../../app/redux/actions/updateLastAction';
import { useAppSelector } from '../../app/redux/hooks';
import { UserMatch, UserMatchesState } from '../../app/redux/reducers/userMatchesReducer';
import { AUTH_LEVEL, ActionType } from '../../app/services/authTypes';
import { SiteMetaData } from '../../types/site';
import { ViewMessages, ViewMessagesCountBadge } from '../account/actions/ViewMessages';
import { UserProfileCompletionProgress } from '../account/UserProfile';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../ui/DropdownMenu';
import SearchInputQuickFilter from './search/SearchInputQuickFilter';
import { SectionContainer } from './sections/SectionContainer';

const defaultInputPlaceholder: string = 'What are you looking for?';
const baseSearchPath: string = '/search';

interface QuickSearch {
  isSearchOpen: boolean;
  onSearchOpen: () => void;
  setIsSearchOpen: (open: boolean) => void;
  siteMetadata: SiteMetaData;
}

const QuickSearch = ({ isSearchOpen, onSearchOpen, setIsSearchOpen, siteMetadata }: QuickSearch) => (
  <Collapsible open={isSearchOpen} onOpenChange={onSearchOpen}>
    <CollapsibleTrigger asChild>
      <Button
        icon
        aria-label={isSearchOpen ? 'Close search' : 'Search'}
        size="sm"
        appearance={isSearchOpen ? 'gray' : 'primary'}
        variant={isSearchOpen ? 'outline' : 'default'}
        className="duration-250"
        data-cy="button:quick-search"
      >
        {isSearchOpen ? <X className={`h-5`} /> : <Search className={`h-4`} />}
      </Button>
    </CollapsibleTrigger>
    <CollapsibleContent className="absolute w-full left-0 top-[60px] bg-background p-4 lg:px-7 lg:rounded-b-2xl border border-gray-200">
      <SectionContainer disableIndent={true} width="2xl" containerClass="space-y-2 header-extended">
        <h2 className="display-title-lg md:display-title-xl display-title-semibold">Search {siteMetadata.brandName}</h2>
        <SearchInputQuickFilter
          baseSearchPath={baseSearchPath}
          placeholder={defaultInputPlaceholder}
          setIsSearchOpen={setIsSearchOpen}
        />
      </SectionContainer>
    </CollapsibleContent>
  </Collapsible>
);
interface HeaderProps extends React.HTMLAttributes<HTMLElement> {
  location: WindowLocation<unknown>;
  siteMetadata: SiteMetaData;
  user: User;
  isSignedIn: boolean;
  authLevel: AUTH_LEVEL;
  signOut: () => void;
  updateAction: (location: any, action: any, actionData?: any) => any;
  getUser: () => void;
}

/**
 * HeaderActions
 * @return {JSX}
 */
const HeaderActions = ({
  location,
  siteMetadata,
  user,
  isSignedIn = false,
  authLevel,
  signOut,
  updateAction,
  getUser
}: HeaderProps) => {
  const [isSearchOpen, setIsSearchOpen] = React.useState<boolean>(false);
  const [isUserActionsOpen, setIsUserActionsOpen] = React.useState<boolean>(false);
  const { matches } = useAppSelector((state) => state.matches) as UserMatchesState;
  const numMatchesUnread = matches.filter((match: UserMatch) => !match.seen).length;
  useEffect(() => {
    if (isSignedIn && isEmpty(user)) {
      getUser();
    }
  }, [isSignedIn, user]);

  const onSearchOpen = () => setIsSearchOpen(!isSearchOpen);

  /** This is the default state of the header navigation, happens to also be the same as logged out */
  const defaultState = (
    <ul className="flex items-center space-x-2" key={`signedOut`}>
      <li className="[.is-searchPage_&]:hidden lg:[.is-searchPage_&]:invisible" key={`signedOut_search`}>
        <QuickSearch
          isSearchOpen={isSearchOpen}
          setIsSearchOpen={setIsSearchOpen}
          onSearchOpen={onSearchOpen}
          siteMetadata={siteMetadata}
        />
      </li>
      <li className="hidden lg:block py-2 px-0.5 h-full [.is-searchPage_&]:hidden lg:[.is-searchPage_&]:invisible">
        <span className="bg-gray-200 w-[1px] h-full block"></span>
      </li>
      <li className="leading-4 lg:hidden">
        <Button asChild appearance="primary" size="sm">
          <Link to="/login" key={`signedOut_login-m`} data-cy={`link:view-log-in`}>
            Log in
          </Link>
        </Button>
      </li>
      <li className="hidden leading-4 lg:block">
        <Button asChild appearance="primary" size="sm">
          <Link to="/login" key={`signedOut_login`} data-cy={`link:lg:view-log-in`}>
            Log in
          </Link>
        </Button>
      </li>
      <li className="hidden lg:block">
        <Button asChild variant="default" appearance={null} size="sm" className="w-full">
          {process.env.GATSBY_SITE_NAME === 'targetjobs' ? (
            <a
              key={`signedOut_hiring`}
              href="https://www.targetconnect.com/employers"
              data-cy={`link:post-job`}
              target="_blank"
            >
              <SquareArrowOutUpRight className="w-4" />
              <span>Post a job</span>
            </a>
          ) : (
            <Link key={`signedOut_hiring`} to="/hiring-organisations" data-cy={`link:post-job`}>
              Post a job
            </Link>
          )}
        </Button>
      </li>
    </ul>
  );

  return isSignedIn ? (
    <ul className="flex items-center space-x-1.5" key={`signedIn`}>
      <li key={`signedIn_search`} className="[.is-searchPage_&]:hidden lg:[.is-searchPage_&]:invisible">
        <QuickSearch
          isSearchOpen={isSearchOpen}
          setIsSearchOpen={setIsSearchOpen}
          onSearchOpen={onSearchOpen}
          siteMetadata={siteMetadata}
        />
      </li>
      <li className="hidden lg:block py-2 px-0.5 h-full [.is-searchPage_&]:hidden lg:[.is-searchPage_&]:invisible">
        <span className="bg-gray-200 w-[1px] h-full  block"></span>
      </li>
      <li className="h-9 hidden lg:block">
        <Button asChild aria-label="Feed" size="sm" appearance="primary" variant="outline">
          <Link to="/dashboard" key={`signedIn_feed`} data-cy={`link:view-feed`}>
            My dashboard
          </Link>
        </Button>
      </li>
      <li key={`signedIn_user`} className="h-9 hidden lg:block">
        <DropdownMenu open={isUserActionsOpen} onOpenChange={setIsUserActionsOpen}>
          <DropdownMenuTrigger asChild>
            <Button icon aria-label="User menu" size="sm" variant="outline" appearance="primary">
              <User className="w-4 h-4" />
              {isSignedIn && numMatchesUnread > 0 && (
                <ViewMessagesCountBadge className="absolute -top-2 -right-2">{numMatchesUnread}</ViewMessagesCountBadge>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" sideOffset={12} className="rounded-t-none space-y-2">
            {user && (
              <>
                <Loader loaded={!!user}>
                  <div className="space-y-1.5 pt-1.5 pb-2.5">
                    <p className="text-gray font-bold leading-6">{getUserProfileDisplayName(user, authLevel)}</p>
                    <UserProfileCompletionProgress
                      user={user}
                      buttonProps={{ onClick: () => setIsUserActionsOpen(false) }}
                    />
                  </div>
                </Loader>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuGroup>
              {isSignedIn && (
                <DropdownMenuItem asChild>
                  <ViewMessages className="text-foreground justify-start px-2 py-1.5 space-x-2 font-normal w-full">
                    {(_numberOfMatches: number, numMatchesUnread: number) => {
                      return (
                        <div className="flex justify-between items-center w-full">
                          <span>Messages</span>
                          {numMatchesUnread > 0 && <ViewMessagesCountBadge>{numMatchesUnread}</ViewMessagesCountBadge>}
                        </div>
                      );
                    }}
                  </ViewMessages>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem asChild>
                <Link
                  to={'/dashboard/profile'}
                  onClick={() => {
                    authLevel === AUTH_LEVEL.PARTIAL &&
                      updateAction({ pathname: '/dashboard/profile', search: '' }, ActionType.VIEW);
                  }}
                  data-cy={`link:view-profile`}
                >
                  My profile
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link
                  to={'/dashboard/settings'}
                  onClick={() => {
                    authLevel === AUTH_LEVEL.PARTIAL &&
                      updateAction({ pathname: '/dashboard/settings', search: '' }, ActionType.VIEW);
                  }}
                  data-cy={`link:view-account`}
                >
                  Account settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to={location.pathname} onClick={() => signOut()} data-cy={`link:log-out`}>
                  Log out
                </Link>
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </li>
    </ul>
  ) : (
    defaultState
  );
};

const mapStateToProps = ({ auth }: any) => {
  return {
    user: auth.user,
    authLevel: auth.authLevel
  };
};

const mapDispatchToProps = {
  updateAction: updateLastAction,
  getUser: getUserAction,
  signOut: signOutAction
};

export default React.memo(connect(mapStateToProps, mapDispatchToProps)(withLocation(HeaderActions)));
